import type {
  CmmdCloudProjectLifecyclePreflight,
  CmmdRuntimeDiagnosticsSnapshot,
  RuntimeDiagnosticsResponse,
} from "@cmmd-forge/contracts";

import { DiagnosticsCloudRuntimes } from "./DiagnosticsCloudRuntimes";
import { DiagnosticsDesktopRuntime } from "./DiagnosticsDesktopRuntime";
import { DiagnosticsLocalServer } from "./DiagnosticsLocalServer";
import type { LocalServerDiagnostics } from "./useLocalServerDiagnostics";
import {
  isDesktopDiagnosticsTab,
  type DesktopDiagnosticsTarget,
} from "./DiagnosticsDesktopTabs.logic";
import type { CloudTraceDiagnostics } from "./DiagnosticsCloudRuntimes";
import { DiagnosticsEvents } from "./DiagnosticsEvents";
import type { DiagnosticsTab } from "./DiagnosticsSettings.types";
import type { useCloudRuntimeDiagnostics } from "./useCloudRuntimeDiagnostics";
import type { RuntimeAuthorityRenewalState } from "../../environments/runtime/cloudRuntimeAuthorityRenewal";

/**
 * The CMMD-attested read, which the cloud and desktop branches share and the
 * server branch has no use for.
 */
export interface CanonicalSnapshotProps {
  readonly snapshot: CmmdRuntimeDiagnosticsSnapshot | undefined;
  readonly error: string | undefined;
  readonly pending: boolean;
}

/** Everything only the cloud branch reads, grouped so the others do not see it. */
export interface CloudDiagnosticsProps {
  readonly diagnostics: ReturnType<typeof useCloudRuntimeDiagnostics>;
  readonly runtimeErrors: ReadonlyArray<{
    readonly label: string;
    readonly error: string;
    readonly at: string | null;
  }>;
  readonly lifecycleReceipt: CmmdCloudProjectLifecyclePreflight | undefined;
  readonly lifecycleError: string | undefined;
  readonly lifecyclePending: boolean;
  readonly providerAuthority: RuntimeAuthorityRenewalState | null;
  readonly traces: CloudTraceDiagnostics;
  readonly inventory: {
    readonly inventory: RuntimeDiagnosticsResponse | undefined;
    readonly error: string | undefined;
    readonly pending: boolean;
    readonly probed: boolean;
  };
  readonly isPlatformAdmin: boolean;
}

export interface LocalServerProps {
  readonly label: string;
  readonly diagnostics: LocalServerDiagnostics;
}

/**
 * One target class per branch. The props are grouped by which branch reads
 * them rather than listed flat, so adding a target class does not widen the
 * signature for the ones that already exist.
 */
export function DiagnosticsSettingsContent({
  activeTab,
  desktopTargets,
  canonical,
  cloud,
  server,
}: {
  readonly activeTab: DiagnosticsTab;
  readonly desktopTargets: ReadonlyArray<DesktopDiagnosticsTarget>;
  readonly canonical: CanonicalSnapshotProps;
  readonly cloud: CloudDiagnosticsProps;
  readonly server: LocalServerProps;
}) {
  if (activeTab === "server") {
    return (
      <DiagnosticsLocalServer
        label={server.label}
        inventory={server.diagnostics.inventory}
        inventoryError={server.diagnostics.inventoryError}
        traces={server.diagnostics.traces}
        tracesError={server.diagnostics.tracesError}
        pending={server.diagnostics.pending}
        probed={server.diagnostics.probed}
      />
    );
  }
  if (activeTab === "cloud") {
    return (
      <DiagnosticsCloudRuntimes
        selectedRuntime={cloud.diagnostics.selectedRuntime}
        canonicalSnapshot={canonical.snapshot}
        canonicalError={canonical.error}
        canonicalPending={canonical.pending}
        lifecycleReceipt={cloud.lifecycleReceipt}
        lifecycleError={cloud.lifecycleError}
        lifecyclePending={cloud.lifecyclePending}
        codexReadiness={cloud.diagnostics.readiness}
        providerAuthority={cloud.providerAuthority}
        traces={cloud.traces}
        machineInventory={cloud.inventory}
        isPlatformAdmin={cloud.isPlatformAdmin}
      />
    );
  }
  if (isDesktopDiagnosticsTab(activeTab)) {
    const desktopTarget = desktopTargets.find((target) => target.tabId === activeTab) ?? null;
    return desktopTarget ? (
      <DiagnosticsDesktopRuntime
        target={desktopTarget}
        snapshot={canonical.snapshot}
        diagnosticsError={canonical.error}
        diagnosticsPending={canonical.pending}
      />
    ) : null;
  }
  return (
    <DiagnosticsEvents
      diagnosticsError={canonical.error}
      cloudRuntimeErrors={cloud.runtimeErrors}
      readinessEvent={cloud.diagnostics.readinessEvent}
    />
  );
}
